// ============================================================================
// ENUMS
// ============================================================================

export enum TaskStatus {
  New = 'New',
  InProgress = 'InProgress',
  Resolved = 'Resolved',
  Blocked = 'Blocked',
}

export enum TaskPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export enum TaskType {
  Custom = 'Custom',
  ProcessGuaranteeApplication = 'ProcessGuaranteeApplication',
  ServiceRequest = 'ServiceRequest',
  ManualAppointmentCancellation = 'ManualAppointmentCancellation',
  CheckPlannedAppointment = 'CheckPlannedAppointment',
  OrphanedOffering = 'OrphanedOffering',
  HealthCheckIn = 'HealthCheckIn',
}

export interface OrphanedOfferingItem {
  id: string;
  name: string;
  code: string;
  externalCode: string;
}

export type HealthCheckInResult = 'Improved' | 'Unchanged' | 'Worsened';

export enum TaskStatusFilter {
  All = 'All',
  New = 'New',
  InProgress = 'InProgress',
  Resolved = 'Resolved',
  Blocked = 'Blocked',
}

// ============================================================================
// RESPONSE INTERFACES - API response types
// ============================================================================

export interface TaskResponse {
  id: string;
  name: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  type?: TaskType;
  idempotencyKey?: string;
  data?: Record<string, any>;
  assigneeId?: string;
  assigneeName?: string;
  assignedAt?: string;
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

// ============================================================================
// DOMAIN MODEL INTERFACES
// ============================================================================

export interface TaskAssignee {
  id: string;
  name: string;
}

export interface TaskDataMap {
  [TaskType.Custom]: Record<string, string>;
  [TaskType.ProcessGuaranteeApplication]: {
    description: string;
    doctorName: string;
    guaranteeId: string;
    memberId: string;
    memberName: string
  };
  [TaskType.ServiceRequest]: {
    serviceCategory: string;
    notes: string;
    memberId: string;
    memberName: string;
  };
  [TaskType.ManualAppointmentCancellation]: {
    memberId: string;
    memberName: string;
    memberPhoneNumber: string;
    partnerId: string;
    partnerName: string;
    partnerPhoneNumbers: string[];
    doctorName: string;
    doctorId: string;
    locationAddress: string;
    locationId: string;
    locationName: string;
    appointmentId: string;
    appointmentStartDate: Date | null;
    serviceName: string;
    serviceCodes: string[];
  };
  [TaskType.CheckPlannedAppointment]: {
    memberId: string;
    memberName: string;
    memberPhoneNumber: string;
    partnerId: string;
    partnerName: string;
    partnerPhoneNumbers: string[];
    doctorName: string;
    doctorId: string;
    locationAddress: string;
    locationId: string;
    locationName: string;
    appointmentId: string;
    appointmentStartDate: Date | null;
    serviceName: string;
    guaranteeId: string;
    prescriptionId: string;
    planningId: string;
    actionId: string;
  };
  [TaskType.OrphanedOffering]: {
    partnerId: string;
    partnerName: string;
    integration: string;
    offerings: OrphanedOfferingItem[];
  };
  [TaskType.HealthCheckIn]: {
    memberId: string;
    memberName: string;
    result: HealthCheckInResult;
    comment: string;
  };
}

export interface Task<T extends TaskType = TaskType> {
  id: string;
  name: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  type: T;
  assignee: TaskAssignee;
  data: TaskDataMap[T];
  createdAt: Date;
  resolution: string;
}

// ============================================================================
// PAYLOAD INTERFACES - Request body types
// ============================================================================

export interface TaskResolvePayload {
  resolution: string;
}

export interface TaskAssignPayload {
  assigneeId: string;
}

export interface TaskCreatePayload {
  // TODO: Add payload fields. LP-7044.
}

// ============================================================================
// SEARCH MODEL INTERFACES - Query/filter types
// ============================================================================

export interface TasksFilter {
  status: TaskStatusFilter;
}

export interface TaskSearchPayload {
  pageSize?: number;
  includeTerminalStatuses?: boolean,
  continuationToken?: string;
  status?: string;
}
